import api from "@/utils/axios";
import { defineStore } from "pinia";

// Keys written to localStorage by the app (besides the persisted "auth" store)
// that must not survive a logout / account switch.
const SESSION_KEYS = [
  "statutsList",
  "newCstmrs",
  "newLivs",
  "newClaims",
  "newPickup",
  "newReturn",
  "ribRequests",
];

/**
 * The API returns permissions as an array of names (["PARCELS", "STOCK", ...]).
 * Older code paths sent an object map ({ PARCELS: true }). Accept both, and
 * return null for anything else so callers can keep the last known list
 * instead of wiping it.
 */
function normalizePermissions(raw: unknown): string[] | null {
  if (Array.isArray(raw)) {
    return raw.filter((p): p is string => typeof p === "string");
  }
  if (raw && typeof raw === "object") {
    const map = raw as Record<string, unknown>;
    return Object.keys(map).filter((key) => Boolean(map[key]));
  }
  return null;
}

// Shared across callers so the startup fetch, the router guard and the
// periodic refresh never fire duplicate requests.
let inFlightFetch: Promise<void> | null = null;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: null as string | null,
    user: null as Record<string, any> | null,
    permissions: [] as string[],
    // True once permissions were confirmed by the server in this page load.
    // Not persisted.
    permissionsSynced: false,
  }),

  getters: {
    isAuthenticated: (state) => Boolean(state.token && state.user?.id),
    permissionSet: (state) => new Set(state.permissions),
  },

  actions: {
    setToken(token: string) {
      this.token = token;
    },
    setUser(user: Record<string, any>) {
      this.user = user;
    },
    setPermissions(permissions: unknown) {
      this.permissions = normalizePermissions(permissions) ?? [];
      this.permissionsSynced = true;
    },

    /** Store the session returned by /login or /authBylHash. */
    setSession(payload: {
      access_token: string;
      user: Record<string, any>;
      permissions: unknown;
    }) {
      this.setToken(payload.access_token);
      this.setUser(payload.user);
      this.setPermissions(payload.permissions);
    },

    clearAuth() {
      this.token = null;
      this.user = null;
      this.permissions = [];
      this.permissionsSynced = false;
      SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
    },

    /** Revoke the token on the server, then clear the local session. */
    async logout() {
      if (this.token) {
        try {
          await api.post("/api/auth/user_logout");
        } catch {
          // Token already invalid or network down: still log out locally.
        }
      }
      this.clearAuth();
    },

    /**
     * Refresh permissions from the server.
     * On a transient failure (network, timeout, 5xx) the last known
     * permissions are kept, so the sidebar does not disappear. A 401 is
     * handled by the axios interceptor, which logs the user out.
     */
    fetchPermissions(): Promise<void> {
      if (!this.isAuthenticated) return Promise.resolve();
      if (inFlightFetch) return inFlightFetch;

      const userId = this.user?.id;
      inFlightFetch = (async () => {
        try {
          const { data } = await api.get(`/api/auth/get_perms_user/${userId}`);
          // Ignore the answer if the user changed while the request was running.
          if (this.user?.id !== userId) return;
          const list = normalizePermissions(data);
          if (list) {
            // Only replace when changed, to avoid re-rendering the sidebar
            // and rewriting localStorage on every refresh.
            if (list.join("|") !== this.permissions.join("|")) {
              this.permissions = list;
            }
            this.permissionsSynced = true;
          } else {
            console.warn("Unexpected permissions payload, keeping previous list");
          }
        } catch (error) {
          console.warn("Failed to refresh permissions, keeping previous list", error);
        } finally {
          inFlightFetch = null;
        }
      })();
      return inFlightFetch;
    },

    can(key: string) {
      return this.permissionSet.has(key);
    },
    canAny(keys: string[]) {
      return keys.some((key) => this.permissionSet.has(key));
    },
    canAll(keys: string[]) {
      return keys.every((key) => this.permissionSet.has(key));
    },
  },

  persist: {
    pick: ["token", "user", "permissions"],
  },
});
